import { setUser as setSentryUser } from '@sentry/react'
import posthog from 'posthog-js'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { useCurrentUserQuery } from '~/graphql/current-user'
import { useSignOutUser } from '~/hooks/use-sign-out-user'
import { useWorkspace } from '~/hooks/use-workspace'
import { CurrentUser } from '~/types'
import { accessToken } from '~/utils/access-token'

type Context = {
  user?: CurrentUser
  loading: boolean
}

const AuthContext = createContext<Context | undefined>(undefined)

export const useAuth = (): Context => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }

  return context
}

type Props = {
  children?: ReactNode
}

export const AuthProvider: FC<Props> = ({ children }) => {
  const signOutUser = useSignOutUser()
  const workspace = useWorkspace()

  const { data, loading } = useCurrentUserQuery({
    ssr: false,
    skip: !accessToken.get('dashboard'),
    onError: () => signOutUser(),
  })

  const value = useMemo<Context>(
    () => ({
      user: data?.currentUser,
      loading,
    }),
    [data, loading]
  )

  useEffect(() => {
    if (data?.currentUser) {
      setSentryUser({
        id: data?.currentUser.id,
        email: data?.currentUser.email,
      })

      // Identify the user and reload feature flags
      posthog.identify(data.currentUser.id, {
        email: data.currentUser.email,
        name: [data.currentUser.firstName, data.currentUser.lastName].filter(Boolean).join(' '),
        firstName: data.currentUser.firstName,
        lastName: data.currentUser.lastName,
        ...(workspace && { workspaceName: workspace.name }),
      })
      posthog.reloadFeatureFlags()
    }
  }, [data, workspace])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
